import React, { RefObject, forwardRef, useImperativeHandle } from "react";
import { FormikConfig, FormikProps } from "formik/dist/types";
import { Form as FormikForm, Formik } from "formik";
import { formContext } from "@montana/shared/form/form.context";

export type IFormBaseProps<TValues> = Omit<FormikConfig<TValues>, "render" | "validationSchema">;

export interface IFormProps<TValues> extends IFormBaseProps<TValues> {
    children: ((props: FormikProps<TValues>) => JSX.Element) | JSX.Element | JSX.Element[];
    novalidate?: boolean;
    ref?: RefObject<Formik<IFormValues>> | ((instance: Formik<IFormValues> | null) => void) | null;
}

export interface IFormRef {
    getForm: () => Formik<IFormValues> | null;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export interface IFormValues extends Object { [key: string]: any }

/**
 * @author SBF & AGI
 *
 * @description
 * Component to handle form logic i.e. submission, form state, form validation, errors.
 *
 * @example
 *  <Form onSubmit={values => handleSubmit(values)}>
 *      <FormTextInput type="text" name="name" placeholder="Name" />
 *      <FormTextInput type="email" name="email" placeholder="Email" />
 *      <button type="submit">Submit</button>
 *  </Form>
 */
export const Form = forwardRef((props: IFormProps<IFormValues>, ref): JSX.Element => {

    const { children, novalidate } = props;

    const formikRef = React.useRef<Formik<IFormValues>>(null);

    useImperativeHandle(ref, () => ({
        getForm: () => formikRef.current
    }));
    
    function registerField<TValue>(name: string, defaultValue: TValue): void {
        if (formikRef?.current) {
            formikRef.current.setFieldValue(name, formikRef.current.initialValues[name] || defaultValue);
        }
    }

    function unRegisterField(name: string): void {
        if (formikRef?.current) {
            const values = { ...formikRef.current.getFormikBag().values };

            if (Object.prototype.hasOwnProperty.call(values, name)) {
                delete values[name];
                formikRef.current.setValues(values);
            }
        }
    }

    const childRenderer = (formikBag: FormikProps<IFormValues>) => (typeof children === "function"
        ? children(formikBag)
        : children);

    return (
        <formContext.Provider value={{ registerField, unRegisterField }}>
            <Formik {...{ ...props, children: undefined }} ref={formikRef}>
                {(formikBag): JSX.Element => (
                    <FormikForm noValidate={novalidate}>
                        {childRenderer(formikBag)}
                    </FormikForm>
                )}
            </Formik>
        </formContext.Provider>
    );
});
